import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateCart } from '../../hooks/useUpdateCart';
import { Cart } from '../../models/Cart';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithCart: (cart: Cart) => void;
}

const EditCartModal = forwardRef<ExtendedModalCenterRef, {}>((props, ref) => {
  const { t } = useTranslation('cartsModule');
  const { toast } = useToast();
  const updateCartMutation = useUpdateCart();

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [cart, setCart] = useState<Cart | null>(null);
  const [code, setCode] = useState('');

  // 🎯 Actualizar campos cuando cambie el carrito interno
  useEffect(() => {
    if (cart) {
      setCode(cart.code);
    }
  }, [cart]);

  // 🎯 Funciones internas - NO necesitan useCallback
  const resetForm = () => {
    setCode('');
    setCart(null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleUpdate = async () => {
    try {
      if (!cart || !code) {
        toast({
          title: t('errors.generic'),
          description: t('modal.edit.error_required'),
          variant: 'destructive',
        });
        return;
      }

      await updateCartMutation.mutateAsync({
        id: cart.id,
        code: code.trim(),
        type_id: cart.type_id, // Mantener el tipo original
      });

      toast({
        title: t('modal.edit.success_title'),
        description: t('modal.edit.success_description'),
      });

      resetForm();
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.edit.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  // 🎯 ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithCart = useCallback((cartData: Cart) => {
    setCart(cartData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithCart, // ← Método nuevo para uso directo
    }),
    [openWithCart],
  );

  const isLoading = updateCartMutation.isPending;

  return (
    <ModalCenter
      ref={internalModalRef}
      title={t('modal.edit.title')}
      type="default"
      width="lg"
      className="h-[calc(100%-30px)] sm:h-auto"
    >
      <div className="space-y-4 flex flex-col justify-between h-full">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-text">{t('modal.edit.code_label')}</label>
            <Input
              className="mt-1"
              placeholder={t('modal.edit.code_placeholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleCancel} name="cancel" size="lg" disabled={isLoading}>
            {t('button.cancel')}
          </Button>
          <Button variant="primary" onClick={handleUpdate} name="confirm" size="lg" disabled={isLoading}>
            {isLoading ? t('button.updating') : t('button.update')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default EditCartModal;
EditCartModal.displayName = 'EditCartModal';
